import React, { useState, useEffect, useMemo } from 'react';
import { useAreas } from '../hooks/useAreas';
import { useTipoAreas } from '../hooks/useTipoAreas';
import { usePisos } from '../hooks/usePisos';
import { useAreaTareas } from '../hooks/useAreaTareas';
import { useTareas } from '../hooks/useTareas';
import { usePagination } from '../hooks/usePagination';
import { Area, CreateAreaForm, UpdateAreaForm, ReordenAreaTareaForm } from '../types';

interface FormErrors {
  nombre?: string;
  descripcion?: string;
  capacidad?: string;
  estado?: string;
  tiempoEstimadoLimpiezaMinutos?: string;
  general?: string;
}

const Areas: React.FC = () => {
  const { 
    areas, 
    isLoading, 
    error,
    createArea, 
    updateArea, 
    deleteArea,
    cambiarDesactivacion,
    isCreating, 
    isUpdating, 
    isDeleting,
    isCambiandoDesactivacion
  } = useAreas();
  
  const { tipoAreas } = useTipoAreas();
  const { pisos } = usePisos();
  // const { tareas } = useTareas(); // No utilizado actualmente
  
  const [showForm, setShowForm] = useState(false);
  const [editingArea, setEditingArea] = useState<Area | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActivo, setFilterActivo] = useState('todos');
  const [filterPisoId, setFilterPisoId] = useState<number | 'todos'>('todos');
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [expandedAreaId, setExpandedAreaId] = useState<number | null>(null);
  
  const [formData, setFormData] = useState<CreateAreaForm>({
    nombre: '',
    descripcion: '',
    capacidad: 1,
    estado: 'Verde',
    tiempoEstimadoLimpiezaMinutos: 30,
    pisoId: undefined,
    tipoAreaId: undefined,
  });

  // Limpiar mensajes de éxito después de 3 segundos
  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage]);

  // Validar formulario
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.nombre.trim()) {
      errors.nombre = 'El nombre es requerido';
    } else if (formData.nombre.length > 200) {
      errors.nombre = 'El nombre no puede exceder 200 caracteres';
    }

    if (formData.descripcion && formData.descripcion.length > 500) {
      errors.descripcion = 'La descripción no puede exceder 500 caracteres';
    }

    if (formData.capacidad <= 0) {
      errors.capacidad = 'La capacidad debe ser mayor a 0';
    }

    if (!['Verde', 'Amarillo', 'Rojo'].includes(formData.estado)) {
      errors.estado = 'El estado debe ser Verde, Amarillo o Rojo';
    }

    // Validar piso seleccionado (requerido)
    if (!formData.pisoId) {
      errors.general = 'Debes seleccionar un piso para crear el área.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Filtrar áreas según búsqueda y filtros (búsqueda global, luego se pagina)
  const filteredAreas = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return areas.filter(area => {
      const matchesSearch = term === '' ||
        area.nombre.toLowerCase().includes(term) ||
        (area.descripcion?.toLowerCase().includes(term) ?? false);
      const matchesActivo = filterActivo === 'todos' ||
        (filterActivo === 'activos' && area.activo) ||
        (filterActivo === 'inactivos' && !area.activo);
      const areaPisoId = area.pisoId ?? area.piso?.id;
      const matchesPiso = filterPisoId === 'todos' || areaPisoId === filterPisoId;
      return matchesSearch && matchesActivo && matchesPiso;
    });
  }, [areas, searchTerm, filterActivo, filterPisoId]);

  const { page, setPage, totalItems, totalPages, pageStart, pageEnd, pageItems, goPrevPage, goNextPage } = usePagination(filteredAreas);

  // Reset de página al cambiar filtros/búsqueda
  useEffect(() => {
    setPage(0);
  }, [searchTerm, filterActivo, filterPisoId, setPage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (editingArea) {
      const updateData: UpdateAreaForm = {
        ...formData,
        activo: editingArea.activo,
        desactivadaManualmente: editingArea.desactivadaManualmente
      };
      updateArea({ id: editingArea.id, area: updateData }, {
        onSuccess: () => {
          setSuccessMessage('Área actualizada exitosamente');
          setShowSuccessMessage(true);
          handleCancel();
        },
        onError: (error: any) => {
          setFormErrors({ general: error.message || 'Error al actualizar el área' });
        }
      });
    } else {
      createArea(formData, {
        onSuccess: () => {
          setSuccessMessage('Área creada exitosamente');
          setShowSuccessMessage(true);
          handleCancel();
        },
        onError: (error: any) => {
          setFormErrors({ general: error.message || 'Error al crear el área' });
        }
      });
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    initializeForm();
  };

  const initializeForm = () => {
    setFormData({
      nombre: '',
      descripcion: '',
      capacidad: 1,
      estado: 'Verde',
      tiempoEstimadoLimpiezaMinutos: 30,
      pisoId: undefined,
      tipoAreaId: undefined,
      desactivadaManualmente: false,
    });
    setFormErrors({});
    setEditingArea(null);
  };

  const handleEdit = (area: Area) => {
    setEditingArea(area);
    setFormData({
      nombre: area.nombre,
      descripcion: area.descripcion || '',
      capacidad: area.capacidad,
      estado: area.estado, // Usar el estado actual del área
      tiempoEstimadoLimpiezaMinutos: area.tiempoEstimadoLimpiezaMinutos,
      pisoId: area.pisoId,
      tipoAreaId: area.tipoAreaId,
      desactivadaManualmente: area.desactivadaManualmente,
    });
    
    setFormErrors({});
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta área? Esta acción no se puede deshacer.')) {
      try {
        await deleteArea(id);
        setSuccessMessage('Área eliminada exitosamente');
        setShowSuccessMessage(true);
      } catch (error) {
      }
    }
  };



  const handleToggleDesactivacionManual = async (area: Area) => {
    try {
      await cambiarDesactivacion({ 
        id: area.id, 
        desactivacion: { 
          desactivadaManualmente: !area.desactivadaManualmente,
          motivo: !area.desactivadaManualmente ? 'Desactivada manualmente por administrador' : undefined
        }
      });
      setSuccessMessage(`Área ${!area.desactivadaManualmente ? 'pausada' : 'reactivada'} exitosamente`);
      setShowSuccessMessage(true);
    } catch (error) {
    }
  };

  const handleInputChange = (field: keyof CreateAreaForm, value: string | number | undefined) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (formErrors[field as keyof FormErrors]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
    // Limpiar error general cuando el usuario empiece a escribir
    if (formErrors.general) {
      setFormErrors(prev => ({ ...prev, general: undefined }));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <div className="text-gray-500 mt-2">Cargando áreas...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error al cargar áreas</h3>
                <div className="mt-2 text-sm text-red-700">
                  {error.message || 'Ha ocurrido un error inesperado. Por favor, intenta de nuevo.'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-2 sm:px-4 lg:px-8">
      <div className="py-6">
        {/* Mensaje de éxito */}
        {showSuccessMessage && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900">Gestión de Áreas</h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Administra las áreas de la clínica</p>
          </div>
          <button
            onClick={() => {
              initializeForm();
              setShowForm(true);
            }}
            className="bg-indigo-600 text-white px-4 py-3 w-full sm:w-auto rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
          >
            Nueva Área
          </button>
        </div>

        {/* Filtros y búsqueda */}
        <div className="bg-white shadow rounded-lg p-3 sm:p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Buscar
              </label>
              <input
                type="text"
                placeholder="Buscar por nombre o descripción..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Piso
              </label>
              <select
                value={filterPisoId}
                onChange={(e) => setFilterPisoId(e.target.value === 'todos' ? 'todos' : parseInt(e.target.value))}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm"
              >
                <option value="todos">Todos los pisos</option>
                {pisos.map((p) => (
                  <option key={p.id} value={p.id}>{p.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Estado
              </label>
              <select
                value={filterActivo}
                onChange={(e) => setFilterActivo(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm"
              >
                <option value="todos">Todas las áreas</option>
                <option value="activos">Solo activas</option>
                <option value="inactivos">Solo inactivas</option>
              </select>
            </div>
            <div className="flex items-end">
              <div className="text-xs sm:text-sm text-gray-600">
                {totalItems} área{totalItems !== 1 ? 's' : ''} encontrada{totalItems !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>

        {showForm && (
          <div className="bg-white shadow rounded-lg p-3 sm:p-6 mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {editingArea ? 'Editar Área' : 'Nueva Área'}
            </h2>
            <form onSubmit={handleSubmit}>
              {/* Error general */}
              {formErrors.general && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                  <div className="flex">
                    <svg className="flex-shrink-0 h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{formErrors.general}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm ${
                      formErrors.nombre ? 'border-red-300' : ''
                    }`}
                  />
                  {formErrors.nombre && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.nombre}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Descripción
                  </label>
                  <textarea
                    value={formData.descripcion}
                    onChange={(e) => handleInputChange('descripcion', e.target.value)}
                    rows={3}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm ${
                      formErrors.descripcion ? 'border-red-300' : ''
                    }`}
                  />
                  {formErrors.descripcion && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.descripcion}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Capacidad *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={formData.capacidad}
                    onChange={(e) => handleInputChange('capacidad', parseInt(e.target.value))}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm ${
                      formErrors.capacidad ? 'border-red-300' : ''
                    }`}
                  />
                  {formErrors.capacidad && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.capacidad}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Estado *
                  </label>
                  <select
                    value={formData.estado}
                    onChange={(e) => handleInputChange('estado', e.target.value)}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm ${
                      formErrors.estado ? 'border-red-300' : ''
                    }`}
                  >
                    <option value="Verde">Verde</option>
                    <option value="Amarillo">Amarillo</option>
                    <option value="Rojo">Rojo</option>
                  </select>
                  {formErrors.estado && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.estado}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {editingArea ? 'Puedes cambiar el estado manualmente' : 'El estado se calculará automáticamente según la limpieza'}
                  </p>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Tipo de Área
                  </label>
                  <select
                    value={formData.tipoAreaId || ''}
                    onChange={(e) => handleInputChange('tipoAreaId', e.target.value ? parseInt(e.target.value) : undefined)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm"
                  >
                    <option value="">Seleccionar tipo de área</option>
                    {tipoAreas.map((tipoArea) => (
                      <option key={tipoArea.id} value={tipoArea.id}>
                        {tipoArea.nombre}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Piso
                  </label>
                  <select
                    value={formData.pisoId || ''}
                    onChange={(e) => handleInputChange('pisoId', e.target.value ? parseInt(e.target.value) : undefined)}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm"
                  >
                    <option value="">Seleccionar piso</option>
                    {pisos.map((piso) => (
                      <option key={piso.id} value={piso.id}>
                        {piso.nombre}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Tiempo Estimado de Limpieza (minutos) *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    max="480"
                    value={formData.tiempoEstimadoLimpiezaMinutos}
                    onChange={(e) => handleInputChange('tiempoEstimadoLimpiezaMinutos', parseInt(e.target.value))}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm ${
                      formErrors.tiempoEstimadoLimpiezaMinutos ? 'border-red-300' : ''
                    }`}
                  />
                  {formErrors.tiempoEstimadoLimpiezaMinutos && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.tiempoEstimadoLimpiezaMinutos}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Tiempo estimado en minutos para completar la limpieza de esta área
                  </p>
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="order-2 sm:order-1 bg-gray-300 text-gray-700 px-4 py-3 w-full sm:w-auto rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isCreating || isUpdating}
                  className="order-1 sm:order-2 bg-indigo-600 text-white px-4 py-3 w-full sm:w-auto rounded-md hover:bg-indigo-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
                >
                  {isCreating || isUpdating ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Guardando...
                    </div>
                  ) : (
                    'Guardar'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {totalItems === 0 ? (
            <div className="px-4 py-8 text-center">
              <div className="text-gray-500">
                {searchTerm || filterActivo !== 'todos' 
                  ? 'No se encontraron áreas que coincidan con los filtros.' 
                  : 'No hay áreas disponibles.'}
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {pageItems.map((area) => (
                <li key={area.id}>
                  <div className="px-3 py-3 sm:px-4 sm:py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
                            area.activo ? 'bg-green-500' : 'bg-gray-400'
                          }`}>
                            <span className="text-white text-sm font-medium">
                              {area.nombre.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-3 sm:ml-4">
                          <div className="text-sm sm:text-base font-medium text-gray-900">
                            {area.nombre}
                          </div>
                          {area.descripcion && (
                            <div className="text-xs sm:text-sm text-gray-500">
                              {area.descripcion}
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2 sm:gap-3 mt-1">
                            <div className={`text-xs sm:text-sm px-2 py-1 rounded-full ${
                              area.estado === 'Verde' ? 'text-green-600 bg-green-100' :
                              area.estado === 'Amarillo' ? 'text-yellow-600 bg-yellow-100' :
                              'text-red-600 bg-red-100'
                            }`}>
                              {area.estado}
                            </div>
                            <div className="text-xs sm:text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                              Capacidad: {area.capacidad}
                            </div>
                            {area.tipoArea && (
                              <div className="text-xs sm:text-sm text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                                {area.tipoArea.nombre}
                              </div>
                            )}
                            {area.piso && (
                              <div className="text-xs sm:text-sm text-orange-600 bg-orange-100 px-2 py-1 rounded-full">
                                {area.piso.nombre}
                              </div>
                            )}
                            {area.areaTareas && area.areaTareas.length > 0 && (
                              <div className="text-xs sm:text-sm text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                                {(() => {
                                  const heredadas = area.areaTareas!.filter(t => t.heredada && !t.excluida && t.activo).length;
                                  const personalizadas = area.areaTareas!.filter(t => !t.heredada && t.activo).length;
                                  const excluidas = area.areaTareas!.filter(t => t.excluida || !t.activo).length;
                                  return `Tareas: ${heredadas} heredadas, ${personalizadas} personalizadas, ${excluidas} excluidas`;
                                })()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <button
                          onClick={() => setExpandedAreaId(prev => prev === area.id ? null : area.id)}
                          className="text-blue-600 hover:text-blue-900 text-sm font-medium py-3 px-3 w-full sm:w-auto text-center rounded-md"
                        >
                          {expandedAreaId === area.id ? 'Ocultar Tareas' : 'Gestionar Tareas'}
                        </button>

                        <button
                          onClick={() => handleToggleDesactivacionManual(area)}
                          disabled={isCambiandoDesactivacion}
                          className={`text-sm font-medium py-3 px-3 w-full sm:w-auto text-center rounded-md ${
                            area.desactivadaManualmente 
                              ? 'text-green-600 hover:text-green-900' 
                              : 'text-purple-600 hover:text-purple-900'
                          } disabled:opacity-50`}
                        >
                          {area.desactivadaManualmente ? 'Reactivar' : 'Pausar'}
                        </button>
                        <button
                          onClick={() => handleEdit(area)}
                          className="text-indigo-600 hover:text-indigo-900 text-sm font-medium py-3 px-3 w-full sm:w-auto text-center rounded-md"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(area.id)}
                          disabled={isDeleting}
                          className="text-red-600 hover:text-red-900 text-sm font-medium disabled:opacity-50 py-3 px-3 w-full sm:w-auto text-center rounded-md"
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>

                    {expandedAreaId === area.id && (
                      <AreaTasksPanel areaId={area.id} />
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Paginación */}
        {totalItems > 0 && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-xs sm:text-sm text-gray-600">
              Mostrando {pageStart + 1}–{pageEnd} de {totalItems}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={goPrevPage}
                disabled={page === 0}
                className="px-3 py-2 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40"
                aria-label="Página anterior"
              >
                ← Anterior
              </button>
              <span className="text-xs sm:text-sm text-gray-600">Página {page + 1} de {totalPages}</span>
              <button
                onClick={goNextPage}
                disabled={page >= totalPages - 1}
                className="px-3 py-2 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40"
                aria-label="Página siguiente"
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AreaTasksPanel: React.FC<{ areaId: number }> = ({ areaId }) => {
  const { areaTareas, isLoading, addAreaTarea, excludeAreaTarea, reinstateAreaTarea, reorderAreaTareas, updateAreaTareaConfig, deleteAreaTareaPersonalizada, isAdding, isExcluding, isReinstating, isReordering, isUpdatingConfig, isDeleting } = useAreaTareas(areaId);
  const { tareas } = useTareas();
  const [selectedTareaId, setSelectedTareaId] = useState<number | ''>('');

  const opcionesTareas = React.useMemo(() => {
    const asignadas = new Set(areaTareas.map(at => at.tareaId));
    return tareas.filter(t => !asignadas.has(t.id));
  }, [tareas, areaTareas]);

  const handleAdd = () => {
    if (typeof selectedTareaId === 'number') {
      addAreaTarea({ tareaId: selectedTareaId, orden: (areaTareas[areaTareas.length - 1]?.orden || 0) + 1, obligatoria: false });
      setSelectedTareaId('');
    }
  };

  const handleReorder = () => {
    const ordenes: ReordenAreaTareaForm[] = areaTareas.map((at, idx) => ({ tareaId: at.tareaId, orden: idx + 1 }));
    reorderAreaTareas(ordenes);
  };

  if (isLoading) {
    return <div className="p-3 text-gray-500 text-sm">Cargando tareas del área...</div>;
  }

  return (
    <div className="mt-3">
      <div className="bg-gray-50 p-3 sm:p-4 rounded-md mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
        <div className="sm:col-span-2">
          <label className="block text-xs sm:text-sm text-gray-700">Agregar tarea</label>
          <select value={selectedTareaId} onChange={(e) => setSelectedTareaId(e.target.value ? Number(e.target.value) : '')} className="mt-1 w-full border rounded px-2 py-2 text-xs sm:text-sm">
            <option value="">Seleccionar tarea</option>
            {opcionesTareas.map(t => (
              <option key={t.id} value={t.id}>{t.nombre}</option>
            ))}
          </select>
        </div>
        <button disabled={isAdding || !selectedTareaId} onClick={handleAdd} className={`px-4 py-3 w-full sm:w-auto rounded text-sm ${isAdding || !selectedTareaId ? 'bg-indigo-300 text-white' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}>Agregar</button>
      </div>

      <div className="bg-white rounded border w-full overflow-x-auto">
        <table className="min-w-full text-xs sm:text-sm">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th className="px-3 sm:px-4 py-2">Orden</th>
              <th className="px-3 sm:px-4 py-2">Nombre</th>
              <th className="px-3 sm:px-4 py-2">Heredada</th>
              <th className="px-3 sm:px-4 py-2">Obligatoria</th>
              <th className="px-3 sm:px-4 py-2">Activa</th>
              <th className="px-3 sm:px-4 py-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {areaTareas.map((at, idx) => (
              <tr key={at.id} className="border-t">
                <td className="px-3 sm:px-4 py-2">{idx + 1}</td>
                <td className="px-3 sm:px-4 py-2">{at.tareaNombre}</td>
                <td className="px-3 sm:px-4 py-2">{at.heredada ? 'Sí' : 'No'}</td>
                <td className="px-3 sm:px-4 py-2">
                  <input type="checkbox" checked={at.obligatoria} onChange={(e) => updateAreaTareaConfig({ tareaId: at.tareaId, payload: { obligatoria: e.target.checked } })} disabled={isUpdatingConfig} />
                </td>
                <td className="px-3 sm:px-4 py-2">
                  <input type="checkbox" checked={at.activo} onChange={(e) => updateAreaTareaConfig({ tareaId: at.tareaId, payload: { activo: e.target.checked } })} disabled={isUpdatingConfig} />
                </td>
                <td className="px-3 sm:px-4 py-2">
                  <div className="flex flex-col sm:flex-row gap-2">
                    {at.excluida ? (
                      <button disabled={isReinstating} className="text-green-700 py-2 px-3 rounded-md text-xs" onClick={() => reinstateAreaTarea(at.tareaId)}>Reincorporar</button>
                    ) : (
                      <>
                        <button disabled={isExcluding} className="text-orange-700 py-2 px-3 rounded-md text-xs" onClick={() => excludeAreaTarea(at.tareaId)}>
                          {at.heredada ? 'Excluir' : 'Excluir'}
                        </button>
                        {!at.heredada && (
                          <button 
                            disabled={isDeleting} 
                            className="text-red-700 py-2 px-3 rounded-md text-xs" 
                            onClick={() => {
                              if (window.confirm(`¿Eliminar completamente la tarea "${at.tareaNombre}" de esta área? Esta acción no se puede deshacer.`)) {
                                deleteAreaTareaPersonalizada(at.tareaId);
                              }
                            }}
                          >
                            {isDeleting ? 'Eliminando...' : 'Eliminar'}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  {at.heredada && (
                    <div className="text-xs text-gray-500 mt-1">Tarea del tipo de área</div>
                  )}
                  {!at.heredada && (
                    <div className="text-xs text-blue-600 mt-1">Tarea personalizada</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3">
        <button disabled={isReordering} onClick={handleReorder} className={`text-sm ${isReordering ? 'text-gray-400' : 'text-gray-700 hover:underline'}`}>Guardar orden actual</button>
      </div>
    </div>
  );
};

export default Areas; 